import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import type { ClosingTracker } from '../../../types/deal';
import { useReferenceLists } from '../../../context/GlobalGetLists';
import { apiGet, apiPost, apiPostFile } from '../../../services/genesisApi/httpsMethods';
import { useErrorDialog } from '../../../hooks/useErrorDialog';
import { ErrorDialog } from '../../../components/common/errorDialog';
import { CustomButton } from '../../../components/common/customButton';
import { CustomIconButton } from '../../../components/common/customIconButton';
import { dateFormatter, normaliseToDateInput } from '../../../utils/numberFormatter';
import { parseDateOnly } from '../../../utils/formatDate';
import '../../../assets/css/AGGrid-custom.css';
import pencilIcon from '@/assets/svg/edit.svg';
import { useCurrentUser } from '@/context/CurrentUserContext';
import { useTenantFeatures } from '@/context/TenantFeaturesContext';
import { fieldExportValue, proceedsExportValue } from './closingTrackerExport';
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

ModuleRegistry.registerModules([AllCommunityModule]);

type DealColumn = {
  dealId: number;
  dealName: string;
  isClosed: boolean;
  statusName: string;
  accordionName: string | null;
  tracker: ClosingTracker;
};

/**
 * Finds sort_order for a status label from the /status reference list.
 * If we can't find it, use a big number so it sorts to the end of that group.
 */
function getSortOrderForStatusName(
  statusName: string,
  statusRows: { status: string; sort_order: number }[],
): number {
  const trimmed = statusName.trim();
  if (trimmed === '') return 999999;

  for (let i = 0; i < statusRows.length; i++) {
    if (statusRows[i].status === trimmed) {
      return statusRows[i].sort_order;
    }
  }

  const lower = trimmed.toLowerCase();
  for (let i = 0; i < statusRows.length; i++) {
    if (statusRows[i].status.toLowerCase() === lower) {
      return statusRows[i].sort_order;
    }
  }

  return 999999;
}

/**
 * Order columns left-to-right:
 * 1) Open deals first, closed deals last
 * 2) For open deals only: pipeline status order (sort_order from /status)
 * 3) Then: target_closing (later date first), then deal name
 */
function sortDealColumns(
  cols: DealColumn[],
  statusRows: { status: string; sort_order: number }[],
): DealColumn[] {
  const copy = [...cols];
  copy.sort((a, b) => {
    // 1) Open deals on the left, closed on the right
    if (a.isClosed === true && b.isClosed === false) {
      return 1;
    }
    if (a.isClosed === false && b.isClosed === true) {
      return -1;
    }

    // 2) Both open: order by status list
    if (a.isClosed === false && b.isClosed === false) {
      const orderA = getSortOrderForStatusName(a.statusName, statusRows);
      const orderB = getSortOrderForStatusName(b.statusName, statusRows);
      if (orderA !== orderB) {
        return orderA - orderB;
      }
    }

    // 3) target_closing: newer / later date first; empty dates last; same date by name
    const aDateStr = normaliseToDateInput(a.tracker.target_closing);
    const bDateStr = normaliseToDateInput(b.tracker.target_closing);
    const aMs = aDateStr ? new Date(aDateStr).getTime() : null;
    const bMs = bDateStr ? new Date(bDateStr).getTime() : null;

    if (aMs !== null && bMs !== null) {
      if (aMs === bMs) {
        return a.dealName.localeCompare(b.dealName);
      }
      return bMs - aMs;
    }
    if (aMs !== null && bMs === null) {
      return -1;
    }
    if (aMs === null && bMs !== null) {
      return 1;
    }
    return a.dealName.localeCompare(b.dealName);
  });
  return copy;
}

/** CSS class for status row colour coding */
function getStatusRowClass(statusName: string): string {
  const s = (statusName || '').trim().toLowerCase();
  if (s === 'closed' || s === 'closed - apollo') return 'closing-tracker-status-closed';
  if (s.includes('closing') || s.includes('in closing')) return 'closing-tracker-status-in-closing';
  if (s.includes('signed') || s.includes('committed')) return 'closing-tracker-status-signed';
  return 'closing-tracker-status-default';
}

/** Format proceeds from closing_tracker (string or number); uses thousand separator when numeric. */
function formatProceeds(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'number') return value.toLocaleString('en-GB');
  const num = Number(String(value).replace(/[,\s]/g, ''));
  if (!Number.isNaN(num)) return num.toLocaleString('en-GB');
  return String(value);
}

type GridRow = {
  rowType: 'accordion' | 'status' | 'meta' | 'section' | 'field';
  label: string;
  fieldKey?: keyof ClosingTracker;
};

const DILIGENCE_FIELDS: { key: keyof ClosingTracker; label: string }[] = [
  { key: 'signed_ts', label: 'Signed TS' },
  { key: 'cost_indemnity_letter', label: 'Cost Indemnity Letter' },
  { key: 'legal', label: 'Legal' },
  { key: 'valuer', label: 'Valuer' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'tech_dd', label: 'Tech DD' },
  { key: 'enviro_dd', label: 'Enviro DD' },
  { key: 'kyc', label: 'KYC' },
  { key: 'project_monitor', label: 'Project Monitor' },
  { key: 'tax', label: 'Tax' },
  { key: 'fdd', label: 'FDD' },
];

/** Proceeds is displayed from loan_amount_day_one; we use a synthetic field key for the row. */
const PROCEEDS_FIELD = 'proceeds' as keyof ClosingTracker;

const INITIAL_PROCESS_FIELDS: { key: keyof ClosingTracker; label: string }[] = [
  { key: 'allocations_request', label: 'Allocations Request' },
  { key: 'memos', label: 'Memos' },
  { key: 'utilisation', label: 'Utilisation' },
  { key: 'closed', label: 'Closed' },
  { key: PROCEEDS_FIELD, label: 'Proceeds' },
];

const DATES_FIELDS: { key: keyof ClosingTracker; label: string }[] = [
  { key: 'proposed_ic_date', label: 'Proposed IC Date' },
  { key: 'target_signing', label: 'Target Signing' },
  { key: 'target_closing', label: 'Target Closing' },
];

const DATE_FIELD_KEYS = new Set<keyof ClosingTracker>(['proposed_ic_date', 'target_signing', 'target_closing']);

/** Build a stable key for a closing tracker so we can diff per row, not per deal. */
function trackerKey(tracker: ClosingTracker | null | undefined): string | null {
  if (!tracker) return null;
  if (tracker.id != null) return `id:${tracker.id}`;
  const dealId = tracker.deal_id ?? 'null';
  const accordionId = tracker.accordion_id ?? 'null';
  return `deal:${dealId}-accordion:${accordionId}`;
}

/** Only these rows are editable in the closing tracker grid. */
const EDITABLE_FIELD_KEYS = new Set<keyof ClosingTracker>([
  'signed_ts',
  'cost_indemnity_letter',
  'legal',
  'valuer',
  'insurance',
  'enviro_dd',
  'kyc',
  'project_monitor',
  'tax',
  'tech_dd',
  'fdd',
  'allocations_request',
  'memos',
  'utilisation',
  'proposed_ic_date',
  'target_signing',
  'target_closing',
]);

function DealHeaderCell(props: { dealId?: number; dealName?: string }) {
  const { dealId, dealName } = props;
  if (dealId == null) return <span>{dealName ?? ''}</span>;
  return (
    <Link to={`/deals/${dealId}`} style={{ color: "#1B7A63", textDecoration: "underline" }}>
      {dealName ?? `Deal ${dealId}`}
    </Link>
  );
}

type ClosingTrackerGridItem = ClosingTracker & {
  deal_name?: string | null;
  status_name?: string | null;
};

export function ClosingTrackerTab() {
  const { canEdit } = useCurrentUser();
  const { isFeatureEnabled } = useTenantFeatures();
  const [columns, setColumns] = useState<DealColumn[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [dirtyGrid, setDirtyGrid] = useState(false);
  const [initialTrackersByKey, setInitialTrackersByKey] = useState<Map<string, ClosingTracker>>(new Map());
  const gridRef = useRef<AgGridReact<GridRow>>(null);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();
  const { status: statusList } = useReferenceLists();

  const fetchDeals = useCallback(() => {
    setIsLoading(true);
    apiGet<ClosingTrackerGridItem[]>('closing-tracker-grid')
      .then((response) => {
        if (response.success && response.data) {
          const list = Array.isArray(response.data) ? response.data : [];
          const cols: DealColumn[] = [];
          for (const item of list) {
            if (!item || typeof (item as any).deal_id !== 'number') continue;
            const dealId = (item as any).deal_id as number;
            const dealName = (item as any).deal_name ?? `Deal ${dealId}`;
            const statusObj = (item as any).status as
              | { status?: string; is_closed?: boolean }
              | undefined;
            const statusName = statusObj?.status ?? 'Unknown';
            const normalizedStatusName =
              typeof statusName === 'string' ? statusName.trim().toLowerCase() : '';
            const isClosedFlag =
              (item as any).closed === 'Yes' ||
              statusObj?.is_closed === true ||
              normalizedStatusName === 'closed' ||
              normalizedStatusName === 'closed - apollo';

            const tracker: ClosingTracker = {
              ...(item as ClosingTracker),
              deal_id: item.deal_id ?? dealId,
            };

            cols.push({
              dealId,
              dealName,
              isClosed: isClosedFlag,
              statusName: String(statusName),
              accordionName: (item as any).accordion_name ?? null,
              tracker,
            });
          }
          const ordered = sortDealColumns(cols, statusList ?? []);
          setColumns(ordered);
          const initialByKey = new Map<string, ClosingTracker>();
          for (const col of ordered) {
            const key = trackerKey(col.tracker);
            if (!key) continue;
            initialByKey.set(key, col.tracker);
          }
          setInitialTrackersByKey(initialByKey);
          setDirtyGrid(false);
        } else {
          showErrorFromResponse(response);
        }
      })
      .catch(() => {
        showErrorFromResponse({ success: false, status: 500, message: 'Failed to fetch closing tracker grid.' });
      })
      .finally(() => setIsLoading(false));
  }, [showErrorFromResponse, statusList]);

  useEffect(() => {
    fetchDeals();
    // Intentionally run only on mount to avoid re-fetch loop (fetchDeals ref can change)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If /status loads after we already got grid data, sort again so status order is right
  useEffect(() => {
    if (!statusList?.length) return;
    setColumns((prev) => {
      if (prev.length === 0) return prev;
      return sortDealColumns(prev, statusList);
    });
  }, [statusList]);

  const rowData = useMemo((): GridRow[] => {
    const rows: GridRow[] = [
      { rowType: 'accordion', label: 'Addon' },
      { rowType: 'status', label: 'Status' },
      { rowType: 'section', label: 'Diligence' },
      ...DILIGENCE_FIELDS.map((f) => ({ rowType: 'field' as const, label: f.label, fieldKey: f.key })),
      { rowType: 'section', label: 'Initial Process' },
      ...INITIAL_PROCESS_FIELDS.map((f) => ({ rowType: 'field' as const, label: f.label, fieldKey: f.key })),
      { rowType: 'section', label: 'Dates' },
      ...DATES_FIELDS.map((f) => ({ rowType: 'field' as const, label: f.label, fieldKey: f.key })),
    ];
    return rows;
  }, []);

  const updateCell = useCallback((colIndex: number, fieldKey: keyof ClosingTracker, value: string | number | null) => {
    setColumns((prev) => {
      const next = [...prev];
      if (colIndex < 0 || colIndex >= next.length) return prev;
      next[colIndex] = {
        ...next[colIndex],
        tracker: { ...next[colIndex].tracker, [fieldKey]: value },
      };
      return next;
    });
  }, []);

  const columnDefs = useMemo((): ColDef<GridRow>[] => {
    const labelCol: ColDef<GridRow> = {
      headerName: 'TenFifty Closing Tracker',
      field: 'label',
      pinned: 'left',
      width: 200,
      minWidth: 180,
      cellClass: (params) => {
        if (params.data?.rowType === 'accordion') return 'closing-tracker-accordion-label';
        if (params.data?.rowType === 'status') return 'closing-tracker-status-label';
        if (params.data?.rowType === 'section') return 'closing-tracker-section-header';
        if (params.data?.rowType === 'meta') return 'closing-tracker-meta';
        return 'closing-tracker-field';
      },
    };
    const dataCols: ColDef<GridRow>[] = columns.map((col, idx) => ({
      headerName: col.dealName,
      colId: `col-${idx}`,
      headerComponent: DealHeaderCell,
      headerComponentParams: { dealId: col.dealId, dealName: col.dealName },
      minWidth: 120,
      width: 140,
      editable: (params) =>
        editMode &&
        params.data?.rowType === 'field' &&
        params.data?.fieldKey != null &&
        EDITABLE_FIELD_KEYS.has(params.data.fieldKey),
      cellClass: (params) => {
        if (params.data?.rowType === 'accordion') return 'closing-tracker-accordion-cell';
        if (params.data?.rowType === 'status') return getStatusRowClass(col.statusName);
        if (params.data?.rowType === 'section') return 'closing-tracker-section-header';
        if (params.data?.rowType === 'meta') return 'closing-tracker-meta';
        return 'closing-tracker-value-cell';
      },
      cellEditorSelector: (params) => {
        const row = params.data;
        if (row?.rowType === 'field' && row.fieldKey && DATE_FIELD_KEYS.has(row.fieldKey)) {
          return {
            component: 'agDateStringCellEditor',
          };
        }
        return undefined;
      },
      valueGetter: (params) => {
        const row = params.data;
        if (!row) return '';
        if (row.rowType === 'accordion') return col.accordionName ?? '—';
        if (row.rowType === 'status') return col.statusName;
        if (row.rowType === 'field' && row.fieldKey) {
          if (row.fieldKey === 'proceeds') return formatProceeds(col.tracker.loan_amount_day_one ?? col.tracker.proceeds);
          const raw = col.tracker[row.fieldKey];

          // For date fields, normalise to YYYY-MM-DD for the editor
          if (DATE_FIELD_KEYS.has(row.fieldKey)) {
            return normaliseToDateInput(raw as string | null | undefined);
          }

          return raw ?? '';
        }
        return '';
      },
      valueFormatter: (params) => {
        const row = params.data;
        if (!row || row.rowType !== 'field' || !row.fieldKey) return params.value;

        if (DATE_FIELD_KEYS.has(row.fieldKey)) {
          const raw = params.value as string | null | undefined;

          // For non-closed deals, empty date fields should show "Hold"
          if (!raw) {
            return col.isClosed ? '' : 'Hold';
          }

          // Local midnight on the calendar date (new Date('YYYY-MM-DD') is
          // UTC midnight, i.e. the previous day west of UTC).
          const d = parseDateOnly(raw);
          if (Number.isNaN(d.getTime())) {
            return params.value;
          }

          const formatted = dateFormatter({ value: raw });

          // Dates in the past show as "Completed - {date}"
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const valueDay = new Date(d);
          valueDay.setHours(0, 0, 0, 0);

          if (valueDay < today) {
            return `Completed - ${formatted}`;
          }

          return formatted;
        }

        return params.value;
      },
      valueSetter: (params) => {
        const row = params.data;
        if (row?.rowType === 'field' && row.fieldKey) {
          const i = parseInt(params.column.getColId().replace('col-', ''), 10);
          if (row.fieldKey === 'proceeds') {
            const raw = params.newValue ?? '';
            const num = raw === '' ? null : Number(String(raw).replace(/[,\s]/g, ''));
            const value = raw === '' || Number.isNaN(num) ? null : num;
            updateCell(i, 'loan_amount_day_one', value);
          } else {
            if (DATE_FIELD_KEYS.has(row.fieldKey)) {
              const raw = params.newValue;
              const old = params.oldValue as string | null | undefined;

              // If the editor was opened and closed without change, do nothing.
              if (raw === old) {
                return false;
              }

              // User cleared the field and pressed Enter: treat as explicit clear.
              if (typeof raw === 'string' && raw.trim() === '') {
                updateCell(i, row.fieldKey, null);
                return false;
              }

              if (raw instanceof Date) {
                updateCell(i, row.fieldKey, raw.toISOString().split('T')[0]);
                return false;
              }

              if (typeof raw === 'string') {
                // agDateCellEditor with HTML5 date input gives YYYY-MM-DD already,
                // so we can store the trimmed string directly.
                const trimmed = raw.trim();
                if (!trimmed) {
                  updateCell(i, row.fieldKey, null);
                  return false;
                }
                updateCell(i, row.fieldKey, trimmed);
                return false;
              }

              // Unsupported value type – ignore.
              return false;
            } else {
              updateCell(i, row.fieldKey, params.newValue ?? null);
            }
          }
        }
        return false;
      },
    }));
    return [labelCol, ...dataCols];
  }, [columns, updateCell, editMode]);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [columns]);

  // Track whether any closing_tracker row has changed (dirty grid)
  useEffect(() => {
    if (columns.length === 0 || initialTrackersByKey.size === 0) {
      setDirtyGrid(false);
      return;
    }
    let changed = false;
    for (const col of columns) {
      const key = trackerKey(col.tracker);
      if (!key) continue;
      const initial = initialTrackersByKey.get(key);
      if (!initial) {
        changed = true;
        break;
      }
      if (JSON.stringify(initial) !== JSON.stringify(col.tracker)) {
        changed = true;
        break;
      }
    }
    setDirtyGrid(changed);
  }, [columns, initialTrackersByKey]);

  // No dirtyGrid guard: it is set in an effect, so it lags one render behind a
  // just-committed cell edit. An empty payload below already means no changes.
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload: ClosingTracker[] = [];
      for (const col of columns) {
        const key = trackerKey(col.tracker);
        if (!key) continue;
        const initial = initialTrackersByKey.get(key);
        if (!initial || JSON.stringify(initial) !== JSON.stringify(col.tracker)) {
          payload.push(col.tracker);
        }
      }

      if (payload.length === 0) {
        setSaving(false);
        return;
      }

      const res = await apiPost('closing-trackers/sync', payload);
      if (!res.success) {
        showErrorFromResponse(res);
        return;
      }
      fetchDeals();
    } finally {
      setSaving(false);
    }
  }, [columns, fetchDeals, initialTrackersByKey, showErrorFromResponse]);

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSave);

  const handleCancel = useCallback(() => {
    fetchDeals();
    setEditMode(false);
  }, [fetchDeals]);

  const buildClosingGridExportBody = useCallback(() => {
    const labelColumnHeader = 'TenFifty Closing Tracker';
    const columnHeaders = [
      labelColumnHeader,
      ...columns.map((c) => c.accordionName ?? c.dealName ?? ''),
    ];
    const getCellValue = (row: GridRow, col: DealColumn): string | number => {
      if (row.rowType === 'accordion') return col.accordionName ?? '—';
      if (row.rowType === 'status') return col.statusName ?? '—';
      if (row.rowType === 'section') return '';
      if (row.rowType === 'field' && row.fieldKey) {
        if (row.fieldKey === 'proceeds') return proceedsExportValue(col.tracker.loan_amount_day_one ?? col.tracker.proceeds);
        return fieldExportValue(col.tracker[row.fieldKey]);
      }
      return '';
    };
    const rows = rowData.map((row) => [
      row.label,
      ...columns.map((col) => getCellValue(row, col)),
    ]);
    return { columnHeaders, rows };
  }, [columns, rowData]);

  const handleExportToExcel = useCallback(async () => {
    const body = buildClosingGridExportBody();
    try {
      const response = await apiPostFile('closing-grid-export', body);
      if (response.success && response.data) {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        const datetime = new Date().toISOString().replace(/[:.]/g, '-');
        a.href = url;
        a.download = `Closing_Grid_Export_${datetime}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        showErrorFromResponse({
          success: false,
          status: 500,
          message: response.message ?? 'Export failed',
        });
      }
    } catch (error) {
      console.error('Closing grid export error:', error);
      showErrorFromResponse({
        success: false,
        status: 500,
        message: 'Failed to export closing grid. Please try again.',
      });
    }
  }, [buildClosingGridExportBody, showErrorFromResponse]);

  if (isLoading && columns.length === 0) {
    return (
      <Box sx={{ padding: '0px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={18} />
        <Typography sx={{ fontSize: '14px', color: '#182737' }}>Loading closing trackers…</Typography>
      </Box>
    );
  }

  const hasColumns = columns.length > 0;

  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 1, minHeight: 32 }}>
          {editMode ? (
            <>
              <CustomButton
                text="Cancel"
                handleButtonPress={handleCancel}
                isButtonDisabled={saving}
                solid={false}
                widthOverride="100px"
              />
              <CustomButton
                text={saving ? 'Saving…' : 'Save'}
                handleButtonPress={requestSave}
                isButtonDisabled={saving || (!dirtyGrid && !isCellEditing)}
                solid
                widthOverride="100px"
              />
            </>
          ) : (
            <>

              <Box sx={{ height: 32, display: 'flex', alignItems: 'center', '& .MuiIconButton-root': { width: 32, height: 32 } }}>
                <CustomIconButton
                  icon={
                    <Box
                      component="img"
                      src={pencilIcon}
                      alt="Edit closing tracker"
                      sx={{ width: 20, height: 20, display: 'block' }}
                    />
                  }
                  disabled={!canEdit}
                  onClick={() => {
                    if (!canEdit) return;
                    setEditMode(true);
                  }}
                />
              </Box>
              {isFeatureEnabled("closing_tracker_export") && (
                <CustomButton
                  text="Export to Excel"
                  handleButtonPress={handleExportToExcel}
                  isButtonDisabled={!hasColumns}
                  solid={false}
                  widthOverride="140px"
                />
              )}
            </>
          )}
        </Box>
        {!hasColumns ? (
          <Typography sx={{ fontSize: '14px', color: '#182737' }}>No deals for closing grid.</Typography>
        ) : (
          <Box
            className="ag-theme-alpine closing-tracker-grid"
            sx={{
              width: '100%',
              minHeight: 400,
              height: 88 + rowData.length * 26,
            }}
          >
            <AgGridReact<GridRow>
              {...gridEditProps}
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={{ resizable: true, filter: false, sortable: false }}
              rowHeight={26}
              headerHeight={48}
              getRowClass={(params) => {
                if (params.data?.rowType === 'accordion') return 'closing-tracker-accordion-row';
                if (params.data?.rowType === 'status') return 'closing-tracker-status-row';
                if (params.data?.rowType === 'section') return 'closing-tracker-section-row';
                return '';
              }}
              singleClickEdit={true}
            />
          </Box>
        )}
      </Box>
      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </>
  );
}
